// LabelFilterChip.tsx — CTSK-498. Active Group / Collection filter chip for the
// Document Library toolbar. Same visual as the "Related to" chip.
// CTSK-500: kind "Batch" is the upload batch (?ingestBatch=); its value is an
// opaque ULID, so the chip reads "Uploaded together" with the id tail.
// CTSK-501: kind "Folder" is the source folder (?folder=). It carries an
// "Include subfolders" toggle (?includeSubfolders=true) and the chip wording
// says which of the two matches is active. Folder names are case-sensitive.

export function LabelFilterChip({
  kind, value, onClear, includeSubfolders, onToggleSubfolders,
}: {
  kind: 'Group' | 'Collection' | 'Batch' | 'Folder';
  value: string;
  onClear: () => void;
  /** Folder only: the chip reads "(+ subfolders)" while true. */
  includeSubfolders?: boolean;
  /** Folder only: renders the "Include subfolders" toggle next to the chip. */
  onToggleSubfolders?: (next: boolean) => void;
}) {
  const isBatch = kind === 'Batch';
  const isFolder = kind === 'Folder';
  const title = isBatch
    ? `Filtered to files uploaded together (batch ${value})`
    : isFolder
      ? `Filtered to folder “${value}”${includeSubfolders ? ' and its subfolders' : ''}`
      : `Filtered to ${kind.toLowerCase()} “${value}”`;
  const chip = (
    <div
      data-testid={`${kind.toLowerCase()}-filter-chip`}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
        borderRadius: 999, fontSize: 12, maxWidth: 280,
        background: 'color-mix(in srgb, var(--accent-primary) 14%, var(--surface-raised))',
        color: 'var(--accent-primary)',
        border: '1px solid color-mix(in srgb, var(--accent-primary) 40%, transparent)',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {isBatch
          ? <>Uploaded together: <strong>…{value.slice(-6)}</strong></>
          : isFolder
            ? <>Folder: <strong>{value}</strong>{includeSubfolders ? ' (+ subfolders)' : ''}</>
            : <>{kind}: <strong>{value}</strong></>}
      </span>
      <button
        type="button"
        aria-label={isBatch ? 'Clear upload batch filter' : `Clear ${kind.toLowerCase()} filter`}
        onClick={onClear}
        style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
  if (!isFolder || !onToggleSubfolders) return chip;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {chip}
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
        <input
          type="checkbox"
          data-testid="folder-subfolders-toggle"
          checked={!!includeSubfolders}
          onChange={(e) => onToggleSubfolders(e.target.checked)}
        />
        Include subfolders
      </label>
    </span>
  );
}
